import type { z } from 'zod';
import type { EntityType } from './common';
import { ClientIdentitySchema, ClientSchema, IntakeSummarySchema } from './client';
import { CounselorMemoSchema, SessionSchema, TranscriptDiscrepancySchema, TranscriptSchema } from './session';
import {
  CaseFormulationRevisionSchema,
  CaseFormulationSchema,
  ClinicalEvidenceSchema,
  CounselingGoalSchema,
  DiagnosticConsiderationSchema,
  GoalProgressSchema,
  RiskAssessmentSchema,
  SessionAnalysisSchema,
} from './clinical';
import {
  CounselingJournalSchema,
  CounselorReflectionSchema,
  DocumentArtifactSchema,
  NextSessionBriefSchema,
  PsychologicalAssessmentSchema,
  SupervisionNoteSchema,
  VerbatimSelectionSchema,
} from './records';
import {
  AIImportSchema,
  AppSettingsSchema,
  AuditEventSchema,
  FileReferenceSchema,
  GlossaryTermSchema,
  SyncConflictSchema,
  SyncStateSchema,
} from './system';

export * from './common';
export * from './client';
export * from './session';
export * from './clinical';
export * from './records';
export * from './system';

/** entityType → zod 스키마. 저장·복원·migration·fixture 검증이 모두 이 표 하나를 사용한다. */
export const ENTITY_SCHEMAS = {
  Client: ClientSchema,
  ClientIdentity: ClientIdentitySchema,
  IntakeSummary: IntakeSummarySchema,
  Session: SessionSchema,
  Transcript: TranscriptSchema,
  TranscriptDiscrepancy: TranscriptDiscrepancySchema,
  CounselorMemo: CounselorMemoSchema,
  ClinicalEvidence: ClinicalEvidenceSchema,
  SessionAnalysis: SessionAnalysisSchema,
  RiskAssessment: RiskAssessmentSchema,
  CounselingGoal: CounselingGoalSchema,
  GoalProgress: GoalProgressSchema,
  CaseFormulation: CaseFormulationSchema,
  CaseFormulationRevision: CaseFormulationRevisionSchema,
  DiagnosticConsideration: DiagnosticConsiderationSchema,
  CounselingJournal: CounselingJournalSchema,
  NextSessionBrief: NextSessionBriefSchema,
  SupervisionNote: SupervisionNoteSchema,
  DocumentArtifact: DocumentArtifactSchema,
  FileReference: FileReferenceSchema,
  AIImport: AIImportSchema,
  AuditEvent: AuditEventSchema,
  SyncState: SyncStateSchema,
  SyncConflict: SyncConflictSchema,
  PsychologicalAssessment: PsychologicalAssessmentSchema,
  VerbatimSelection: VerbatimSelectionSchema,
  CounselorReflection: CounselorReflectionSchema,
  AppSettings: AppSettingsSchema,
  GlossaryTerm: GlossaryTermSchema,
} as const satisfies Record<EntityType, z.ZodTypeAny>;

export type EntityOf<T extends EntityType> = z.infer<(typeof ENTITY_SCHEMAS)[T]>;
export type AnyEntity = { [K in EntityType]: EntityOf<K> }[EntityType];

export function parseEntity(value: unknown): AnyEntity {
  const type = (value as { entityType?: unknown } | null)?.entityType;
  if (typeof type !== 'string' || !(type in ENTITY_SCHEMAS)) {
    throw new Error(`알 수 없는 entityType: ${String(type)}`);
  }
  return ENTITY_SCHEMAS[type as EntityType].parse(value) as AnyEntity;
}
